import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Source, AiAgent, MastermindSpace, Topic } from "@shared/schema";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";

export interface PersonalizationDetails {
  hasVoiceProfile: boolean;
  voiceProfileFields: string[];
  hasAgent: boolean;
  agentName?: string;
  agentLens?: string;
  sourceCount: number;
  sourceNames: string[];
  spaceCount: number;
  spaceNames: string[];
  hasSpaceLens: boolean;
  hasTopicContext: boolean;
  topicName?: string;
  chatMode: string;
  sourceOfTruthCount?: number;
}

export interface ServerPersonalizationLayers {
  userVoice: boolean;
  agent: { name: string; lens: string | null } | null;
  space: { names: string[]; hasLens: boolean } | null;
  topic: { name: string } | null;
  sources: { count: number } | null;
  chatMode: string;
}

interface UsePersonalizationDetailsParams {
  selectedAgentId: string | null;
  selectedSourceIds: string[];
  selectedSpaceIds: string[];
  selectedTopicId: string | null;
  chatMode: string;
  serverLayers?: ServerPersonalizationLayers | null;
  sourceOfTruthCount?: number;
}

export function usePersonalizationDetails({
  selectedAgentId,
  selectedSourceIds,
  selectedSpaceIds,
  selectedTopicId,
  chatMode,
  serverLayers,
  sourceOfTruthCount = 0,
}: UsePersonalizationDetailsParams): PersonalizationDetails {
  const { profile: userProfile } = useUserProfile();
  const { currentWorkspaceId } = useWorkspace();

  const { data: agents = [] } = useQuery<AiAgent[]>({
    queryKey: ["/api/ai-agents"],
    queryFn: async () => {
      const { aiAgentsService } = await import("@/services/aiAgentsService");
      return aiAgentsService.getAgents();
    },
  });

  const { data: sources = [] } = useQuery<Source[]>({
    queryKey: ["/api/sources", currentWorkspaceId ?? null],
    queryFn: async () => {
      const { sourcesService } = await import("@/services/sourcesService");
      return sourcesService.getSources(undefined, currentWorkspaceId ?? null);
    },
  });

  const { data: spaces = [] } = useQuery<MastermindSpace[]>({
    queryKey: ["/api/spaces", currentWorkspaceId ?? null],
    queryFn: async () => {
      const { mastermindSpacesService } = await import("@/services/mastermindSpacesService");
      return mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
    },
  });

  const { data: topics = [] } = useQuery<Topic[]>({
    queryKey: ["/api/topics", currentWorkspaceId ?? null],
    queryFn: async () => {
      const { topicsService } = await import("@/services/topicsService");
      return topicsService.getAllTopics(currentWorkspaceId ?? null);
    },
  });

  const details = useMemo((): PersonalizationDetails => {
    const voiceProfileFields: string[] = [];
    
    if (userProfile) {
      if (userProfile.tone && userProfile.tone !== "balanced") voiceProfileFields.push("tone");
      if (userProfile.verbosity && userProfile.verbosity !== "balanced") voiceProfileFields.push("verbosity");
      if (userProfile.learningStyle && userProfile.learningStyle !== "mixed") voiceProfileFields.push("learningStyle");
      if (userProfile.responseFormat && userProfile.responseFormat !== "mixed") voiceProfileFields.push("responseFormat");
      if (userProfile.writingStyle) voiceProfileFields.push("writingStyle");
      if (userProfile.expertise && userProfile.expertise.length > 0) voiceProfileFields.push("expertise");
      if (userProfile.background) voiceProfileFields.push("background");
      if (userProfile.worldview) voiceProfileFields.push("worldview");
      if (userProfile.goals && userProfile.goals.length > 0) voiceProfileFields.push("goals");
      if (userProfile.communicationPreferences) voiceProfileFields.push("communicationPreferences");
      if (userProfile.favoritePhrases && userProfile.favoritePhrases.length > 0) voiceProfileFields.push("favoritePhrases");
      if (userProfile.avoidPhrases && userProfile.avoidPhrases.length > 0) voiceProfileFields.push("avoidPhrases");
      if (userProfile.referenceAuthors && userProfile.referenceAuthors.length > 0) voiceProfileFields.push("referenceAuthors");
    }

    const selectedAgent = (selectedAgentId && Array.isArray(agents) ? agents.find(a => a.id === selectedAgentId) : null) as AiAgent | null;

    const selectedSourceNames = Array.isArray(sources)
      ? selectedSourceIds
        .map(id => sources.find(s => s.id === id)?.title)
        .filter((name): name is string => !!name)
      : [];

    const selectedSpaceNames = Array.isArray(spaces)
      ? selectedSpaceIds
        .map(id => spaces.find(s => s.id === id)?.name)
        .filter((name): name is string => !!name)
      : [];

    const selectedTopic = selectedTopicId && Array.isArray(topics) ? topics.find(t => t.id === selectedTopicId) : null;

    if (serverLayers) {
      return {
        hasVoiceProfile: serverLayers.userVoice,
        voiceProfileFields: serverLayers.userVoice ? voiceProfileFields : [],
        hasAgent: !!serverLayers.agent,
        agentName: serverLayers.agent?.name,
        agentLens: serverLayers.agent?.lens || undefined,
        sourceCount: serverLayers.sources?.count || 0,
        sourceNames: selectedSourceNames,
        spaceCount: serverLayers.space?.names.length || 0,
        spaceNames: serverLayers.space?.names || selectedSpaceNames,
        hasSpaceLens: serverLayers.space?.hasLens || false,
        hasTopicContext: !!serverLayers.topic,
        topicName: serverLayers.topic?.name,
        chatMode: serverLayers.chatMode || chatMode,
        sourceOfTruthCount,
      };
    }

    return {
      hasVoiceProfile: voiceProfileFields.length > 0,
      voiceProfileFields,
      hasAgent: !!selectedAgent,
      agentName: selectedAgent?.name,
      agentLens: selectedAgent?.lens || undefined,
      sourceCount: selectedSourceIds.length,
      sourceNames: selectedSourceNames,
      spaceCount: selectedSpaceIds.length,
      spaceNames: selectedSpaceNames,
      hasSpaceLens: false,
      hasTopicContext: !!selectedTopic,
      topicName: selectedTopic?.name,
      chatMode,
      sourceOfTruthCount,
    };
  }, [
    userProfile, 
    selectedAgentId, 
    agents, 
    selectedSourceIds, 
    sources, 
    selectedSpaceIds, 
    spaces, 
    selectedTopicId, 
    topics, 
    chatMode,
    serverLayers,
    sourceOfTruthCount,
  ]);

  return details;
}
